import { useEffect, useRef, useState, type MutableRefObject } from 'react'
import {
  Bike, BookOpenText, Brush, Camera, Clapperboard, Disc3, Drama, Drum, Dumbbell, Flag, Flame, Gamepad2, Ghost, Gift,
  HandHelping, Heart, Landmark, LayoutGrid, Leaf, MicVocal, Moon, Mountain, Music, Palette, PartyPopper, PawPrint,
  Plus, Presentation, ShoppingBasket, Sparkles, Star, Sun, Tags, Tent, Trophy, Utensils,
} from 'lucide-react'
import { CATEGORY_COLOR_LABELS } from '../../../shared/categoryCatalog'
import { isIconNode, type IconNode } from '../../../shared/iconDrawing'
import { cmsApi } from '../lib/api'
import type { CategoryConfig, TaxonomyData, ThemeConfig } from '../types'
import { IconDrawing, IconPicker, shoeNode } from './IconPicker'
import { LeaveDialog } from './LeaveDialog'
import { LoadingState } from './LoadingState'
import { useDragOrder } from './dragOrder'

// The app's own icons, saved by name before drawings travelled with the catalogue.
const ICONS = {
  Bike, BookOpenText, Brush, Camera, Clapperboard, Disc3, Drama, Drum, Dumbbell, Flag, Flame, Gamepad2, Ghost, Gift,
  HandHelping, Heart, Landmark, LayoutGrid, Leaf, MicVocal, Moon, Mountain, Music, Palette, PartyPopper, PawPrint,
  Presentation, ShoppingBasket, Sparkles, Star, Sun, Tent, Trophy, Utensils,
} as Record<string, typeof Star>

type Entry = CategoryConfig | ThemeConfig
type Kind = 'category' | 'theme'

type Props = {
  // Where the CMS's menu asks before leaving with unsaved changes.
  exit: MutableRefObject<((go: () => void) => void) | null>
  onChanged: () => Promise<void>
  onSaved: (message: string) => void
  onError: (message: string) => void
}

const codeOf = (name: string) => name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

const blank = (kind: Kind): Entry => ({
  name: '', slug: '', sort_order: 0, icon_key: 'Star', color_key: '#C8458D',
  ...(kind === 'category'
    ? { catalog_enabled: false, show_in_filters: true, show_in_interests: true }
    : { is_active: false, starts_on: null, ends_on: null }),
})

const isActive = (item: Entry) => ('catalog_enabled' in item ? item.catalog_enabled : item.is_active)
const dayOf = (date: string) => date.split('-').reverse().slice(0, 2).join('/')

// Where a category shows in the app, or a theme's period, as the list row says it.
function summaryOf(item: Entry) {
  if ('catalog_enabled' in item) {
    if (item.show_in_filters && item.show_in_interests) return 'Filtros e Interesses'
    if (item.show_in_filters) return 'Só nos filtros'
    return item.show_in_interests ? 'Só em Interesses' : 'Fora dos filtros e de Interesses'
  }
  if (item.starts_on && item.ends_on) return `${dayOf(item.starts_on)} a ${dayOf(item.ends_on)}`
  if (item.starts_on) return `A partir de ${dayOf(item.starts_on)}`
  return item.ends_on ? `Até ${dayOf(item.ends_on)}` : 'Sem período'
}

export function TaxonomyIcon({ name, node }: { name: string; node?: IconNode | null }) {
  if (isIconNode(node)) return <IconDrawing node={node} />
  // The sports shoe is the app's own drawing, not Lucide's.
  if (name === 'SportShoe') return <IconDrawing node={shoeNode} />
  const Icon = ICONS[name] ?? LayoutGrid
  return <Icon size={20} />
}

export function TaxonomyPage({ exit, onChanged, onSaved, onError }: Props) {
  const [data, setData] = useState<TaxonomyData | null>(null)
  const [kind, setKind] = useState<Kind>('category')
  const [entry, setEntry] = useState<Entry | null>(null)
  const [original, setOriginal] = useState('')
  const [existing, setExisting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [error, setError] = useState('')
  const [scheduled, setScheduled] = useState(false)
  const [leaving, setLeaving] = useState<(() => void) | null>(null)
  const [opened, setOpened] = useState(0)
  const [ordering, setOrdering] = useState(false)
  const orderTimer = useRef<number | null>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const dirty = entry !== null && JSON.stringify(entry) !== original
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty
  const savingRef = useRef(saving)
  savingRef.current = saving

  useEffect(() => {
    const leave = (go: () => void) => {
      if (savingRef.current) return
      if (dirtyRef.current) setLeaving(() => go)
      else go()
    }
    exit.current = leave
    const warn = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current && !savingRef.current) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => {
      if (exit.current === leave) exit.current = null
      window.removeEventListener('beforeunload', warn)
    }
  }, [exit])

  const load = async () => {
    try {
      setData(await cmsApi.taxonomy())
      setLoadError('')
    } catch (requestError) {
      setLoadError(requestError instanceof Error ? requestError.message : 'Não foi possível carregar o catálogo.')
    }
  }

  useEffect(() => { void load() }, [])

  // On a narrow screen the form sits under the list: bring it into view.
  useEffect(() => {
    if (opened && window.matchMedia('(max-width: 900px)').matches) formRef.current?.scrollIntoView({ block: 'start' })
  }, [opened])

  const show = (next: Kind, value: Entry | null) => {
    const item = value ?? blank(next)
    setScheduled(next === 'theme' && Boolean((item as ThemeConfig).starts_on || (item as ThemeConfig).ends_on))
    setKind(next)
    setEntry(item)
    setExisting(value !== null)
    setOriginal(JSON.stringify(item))
    setError('')
    setOpened((count) => count + 1)
  }

  // Switching between categories and themes clears the form.
  const switchTo = (next: Kind) => {
    const go = () => {
      setKind(next)
      setEntry(null)
      setOriginal('')
      setError('')
    }
    if (next === kind || saving) return
    if (dirty) setLeaving(() => go)
    else go()
  }

  const open = (next: Kind, value: Entry | null = null) => {
    if (saving) return
    if (dirty) setLeaving(() => () => show(next, value))
    else show(next, value)
  }

  const change = (values: Partial<Entry>) => setEntry((previous) => (previous ? { ...previous, ...values } as Entry : previous))

  const save = async () => {
    if (!entry || saving) return
    setSaving(true)
    setError('')
    try {
      await cmsApi.saveTaxonomy(kind, entry)
      setOriginal(JSON.stringify(entry))
      setExisting(true)
      setData(await cmsApi.taxonomy())
      await onChanged()
      onSaved(kind === 'category' ? 'Categoria salva.' : 'Tema salvo.')
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Não foi possível salvar.')
    } finally {
      setSaving(false)
    }
  }

  const discard = () => {
    if (existing) show(kind, JSON.parse(original) as Entry)
    else setEntry(null)
  }

  // The order is saved as soon as a row is dropped (Linear, Notion); arrow
  // keys pressed in a row are saved together, a moment after the last one.
  const saveOrder = (listKind: Kind, slugs: string[]) => {
    if (orderTimer.current) window.clearTimeout(orderTimer.current)
    orderTimer.current = window.setTimeout(async () => {
      orderTimer.current = null
      setOrdering(true)
      try {
        await cmsApi.reorderTaxonomy(listKind, slugs)
        setData(await cmsApi.taxonomy())
        await onChanged()
        onSaved('Ordem salva.')
      } catch (orderError) {
        onError(orderError instanceof Error ? orderError.message : 'Não foi possível salvar a ordem.')
        await load()
      } finally {
        setOrdering(false)
      }
    }, 400)
  }

  const move = (from: number, to: number) => {
    if (!data) return
    const key = kind === 'category' ? 'categories' : 'themes'
    const list = [...data[key]] as Entry[]
    const [item] = list.splice(from, 1)
    list.splice(to, 0, item)
    setData({ ...data, [key]: list })
    saveOrder(kind, list.map((row) => row.slug))
  }

  const theme = kind === 'theme' ? entry as ThemeConfig | null : null
  const category = kind === 'category' ? entry as CategoryConfig | null : null
  const items = data ? (kind === 'category' ? data.categories : data.themes) : []
  // The tags the collection receives now, and the theme's own if it left them.
  const sourceTags = data?.source_tags ?? []
  const tagOptions = theme?.source_tag && !sourceTags.some((item) => item.tag === theme.source_tag)
    ? [{ tag: theme.source_tag, events: 0 }, ...sourceTags]
    : sourceTags
  const noun = kind === 'category' ? 'categoria' : 'tema'
  const { dropTarget, handle } = useDragOrder(items.length, move, saving || ordering || items.length < 2)

  return (
    <div className="page-stack">
      <section className="page-heading page-heading--with-action">
        <div>
          <h1>Categorias e temas</h1>
          <p>Categorias organizam os eventos. Temas reúnem uma programação durante uma temporada, como o Carnaval.</p>
        </div>
        <div className="page-heading__actions">
          <button className="button button--primary" disabled={!data || saving} onClick={() => open(kind)}>
            <Plus size={18} /> {kind === 'category' ? 'Nova categoria' : 'Novo tema'}
          </button>
        </div>
      </section>

      <div className="mode-toggle" role="group" aria-label="Tipo de catálogo">
        <button type="button" className={kind === 'category' ? 'is-active' : ''} aria-pressed={kind === 'category'} onClick={() => switchTo('category')}>Categorias</button>
        <button type="button" className={kind === 'theme' ? 'is-active' : ''} aria-pressed={kind === 'theme'} onClick={() => switchTo('theme')}>Temas</button>
      </div>

      {!data ? (
        loadError ? (
          <div className="panel error-state">
            <p>{loadError}</p>
            <button className="button button--secondary" onClick={() => void load()}>Tentar novamente</button>
          </div>
        ) : <LoadingState label="Carregando categorias e temas…" />
      ) : (
        <div className="taxonomy-layout">
          <section className="panel panel--flush" aria-label={kind === 'category' ? 'Categorias cadastradas' : 'Temas cadastrados'}>
            {items.length ? items.map((item, index) => {
              const row = dropTarget(index)
              return (
                <div key={item.slug} {...row}
                  className={['taxonomy-row', entry?.slug === item.slug && existing && 'taxonomy-row--selected', row.className].filter(Boolean).join(' ')}>
                  {handle(index, item.name)}
                  <button type="button" className="taxonomy-row__open" disabled={saving} onClick={() => open(kind, item)}>
                    <span className="producer-avatar"><TaxonomyIcon name={item.icon_key} node={item.icon_node} /></span>
                    <span className="producer-row__main">
                      <strong>{item.name}</strong>
                      <small>{summaryOf(item)}</small>
                    </span>
                    <span className={`status-pill ${isActive(item) ? 'status-pill--published' : 'status-pill--deleted'}`}>{isActive(item) ? 'Ativo' : 'Inativo'}</span>
                  </button>
                </div>
              )
            }) : (
              <div className="empty-state">
                <Tags size={30} />
                <h3>{kind === 'category' ? 'Nenhuma categoria' : 'Nenhum tema'}</h3>
                <p>{kind === 'category' ? 'Crie a primeira categoria do app.' : 'Crie um tema para a próxima temporada.'}</p>
              </div>
            )}
          </section>

          {entry ? (
            <div className="taxonomy-editor">
              <form ref={formRef} id="taxonomy-form" className="panel form-section" onSubmit={(event) => { event.preventDefault(); void save() }}>
                <div className="form-section__heading">
                  <div>
                    <h2>{existing ? `Editar ${noun}` : kind === 'category' ? 'Nova categoria' : 'Novo tema'}</h2>
                    <p>{kind === 'category'
                      ? 'O código fica o mesmo quando o nome muda, e os eventos continuam nela.'
                      : 'O tema de cada evento se escolhe no editor do evento ou pela etiqueta do Ingresso.com.'}</p>
                  </div>
                </div>
                <fieldset className="form-grid" disabled={saving}>
                  <label className="field field--wide">
                    <span>Nome *</span>
                    <input required maxLength={80} value={entry.name}
                      onChange={(event) => change({ name: event.target.value, ...(!existing ? { slug: codeOf(event.target.value) } : {}) })} />
                  </label>
                  <IconPicker key={`${kind}:${entry.slug}`} value={entry.icon_key} wide={!category}
                    preview={<TaxonomyIcon name={entry.icon_key} node={entry.icon_node} />}
                    onChange={(icon) => change({ icon_key: icon.key, icon_node: icon.node })} />
                  {category && (
                    <label className="field">
                      <span>Família de cor</span>
                      <select value={entry.color_key} onChange={(event) => change({ color_key: event.target.value })}>
                        {Object.entries(CATEGORY_COLOR_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                      </select>
                    </label>
                  )}

                  {category && <>
                    <label className="toggle-field toggle-field--wide">
                      <input type="checkbox" checked={category.catalog_enabled} onChange={(event) => change({ catalog_enabled: event.target.checked })} />
                      <span><strong>Categoria ativa</strong><small>Aparece no app e pode ser escolhida nos eventos.</small></span>
                    </label>
                    <label className="toggle-field toggle-field--inline">
                      <input type="checkbox" checked={category.show_in_filters} onChange={(event) => change({ show_in_filters: event.target.checked })} />
                      <span><strong>Mostrar nos filtros</strong><small>Na lista de categorias dos filtros do mapa.</small></span>
                    </label>
                    <label className="toggle-field toggle-field--inline">
                      <input type="checkbox" checked={category.show_in_interests} onChange={(event) => change({ show_in_interests: event.target.checked })} />
                      <span><strong>Mostrar em Interesses</strong><small>Na escolha de interesses da conta.</small></span>
                    </label>
                  </>}

                  {theme && <>
                    <label className="toggle-field toggle-field--wide">
                      <input type="checkbox" checked={theme.is_active} onChange={(event) => change({ is_active: event.target.checked })} />
                      <span><strong>Tema ativo</strong><small>Aparece em Especiais, nos filtros do app, e no lugar da categoria nos eventos dele. Desligar preserva os eventos.</small></span>
                    </label>
                    <label className="toggle-field toggle-field--wide">
                      <input type="checkbox" checked={scheduled} onChange={(event) => {
                        setScheduled(event.target.checked)
                        if (!event.target.checked) change({ starts_on: null, ends_on: null })
                      }} />
                      <span><strong>Agendar período</strong><small>Limita o tema a estas datas, no horário de Brasília. Sem fim, ele fica até você desligar.</small></span>
                    </label>
                    {scheduled && <>
                      <label className="field">
                        <span>Início da temporada</span>
                        <input type="date" value={theme.starts_on ?? ''} onChange={(event) => change({ starts_on: event.target.value || null })} />
                      </label>
                      <label className="field">
                        <span>Fim da temporada</span>
                        <input type="date" min={theme.starts_on || undefined} value={theme.ends_on ?? ''}
                          onChange={(event) => change({ ends_on: event.target.value || null })} />
                      </label>
                    </>}
                    <label className="field field--wide">
                      <span>Etiqueta do Ingresso.com</span>
                      <select value={theme.source_tag ?? ''} onChange={(event) => change({ source_tag: event.target.value || null })}>
                        <option value="">Nenhuma</option>
                        {tagOptions.map(({ tag, events }) => {
                          const usedBy = data?.themes.find((item) => item.source_tag === tag && item.slug !== theme.slug)
                          return (
                            <option key={tag} value={tag} disabled={Boolean(usedBy)}>
                              {tag} · {usedBy ? `no tema ${usedBy.name}` : events === 1 ? '1 filme' : events ? `${events} filmes` : 'sem filmes agora'}
                            </option>
                          )
                        })}
                      </select>
                      <small>Os filmes do Ingresso.com com esta etiqueta entram no tema ao salvar e a cada coleta. O que você mudar no editor do evento vale.</small>
                    </label>
                  </>}
                </fieldset>
                {error && <p className="form-error" role="alert">{error}</p>}
              </form>

              {(dirty || !existing) && (
                <div className="editor-footer" role="region" aria-label="Alterações não salvas">
                  <span role="status">{existing ? 'Alterações não salvas' : kind === 'category' ? 'Nova categoria' : 'Novo tema'}</span>
                  <div>
                    <button type="button" className="button button--secondary" disabled={saving} onClick={discard}>
                      {existing ? 'Descartar alterações' : 'Descartar'}
                    </button>
                    <button type="submit" form="taxonomy-form" className="button button--primary" disabled={saving || !dirty}>
                      {saving ? 'Salvando…' : 'Salvar'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="panel empty-state">
              <Tags size={30} />
              <h3>{kind === 'category' ? 'Escolha uma categoria' : 'Escolha um tema'}</h3>
              <p>Ou crie {kind === 'category' ? 'uma nova categoria' : 'um novo tema'} no botão acima.</p>
            </div>
          )}
        </div>
      )}

      {leaving && (
        <LeaveDialog
          kind="changes"
          entity="taxonomy"
          onDrop={() => { setLeaving(null); leaving() }}
          onClose={() => setLeaving(null)}
        />
      )}
    </div>
  )
}
